import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMenuPermissions } from '../contexts/MenuPermissionsContext';

// Mapeia cada item do menu CRM para sua chave de permissão
const CRM_MENU_KEYS: Record<string, string> = {
  '/conexoes': 'crm.conexoes',
  '/chat-crm': 'crm.chat-crm',
  '/contatos': 'crm.contatos',
  '/funis': 'crm.funis',
  '/formularios': 'crm.formularios',
  '/follow-up': 'crm.follow-up',
  '/campanhas': 'crm.campanhas',
  '/prospeccao': 'crm.prospeccao',
  '/automacoes': 'crm.automacoes',
  '/configuracoes': 'crm.configuracoes',
};

interface NavigationProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Navigation({ isOpen = false, onClose }: NavigationProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { canSee } = useMenuPermissions();
  const [showFunis, setShowFunis] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [showProspeccao, setShowProspeccao] = useState(false);
  const [showAutomacoes, setShowAutomacoes] = useState(false);
  const [showCampanhas, setShowCampanhas] = useState(false);
  const [showConfiguracoes, setShowConfiguracoes] = useState(false);
  const [showChatCrm, setShowChatCrm] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const funisRef = useRef<HTMLDivElement>(null);
  const connectionsRef = useRef<HTMLDivElement>(null);
  const prospeccaoRef = useRef<HTMLDivElement>(null);
  const automacoesRef = useRef<HTMLDivElement>(null);
  const campanhasRef = useRef<HTMLDivElement>(null);
  const configuracoesRef = useRef<HTMLDivElement>(null);
  const chatCrmRef = useRef<HTMLDivElement>(null);
  const contactsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (funisRef.current && !funisRef.current.contains(event.target as Node)) {
        setShowFunis(false);
      }
      if (connectionsRef.current && !connectionsRef.current.contains(event.target as Node)) {
        setShowConnections(false);
      }
      if (prospeccaoRef.current && !prospeccaoRef.current.contains(event.target as Node)) {
        setShowProspeccao(false);
      }
      if (automacoesRef.current && !automacoesRef.current.contains(event.target as Node)) {
        setShowAutomacoes(false);
      }
      if (campanhasRef.current && !campanhasRef.current.contains(event.target as Node)) {
        setShowCampanhas(false);
      }
      if (configuracoesRef.current && !configuracoesRef.current.contains(event.target as Node)) {
        setShowConfiguracoes(false);
      }
      if (chatCrmRef.current && !chatCrmRef.current.contains(event.target as Node)) {
        setShowChatCrm(false);
      }
      if (contactsRef.current && !contactsRef.current.contains(event.target as Node)) {
        setShowContacts(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (location.pathname.startsWith('/marketing-studio')) return null;
  if (location.pathname.startsWith('/business-core')) return null;
  if (location.pathname.startsWith('/seller')) return null;
  if (location.pathname.startsWith('/flow')) return null;

  const menuItems = [
    {
      path: '/conexoes',
      label: 'Conexões',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/whatsapp',
          label: 'WhatsApp Não Oficial',
          icon: (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.885-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          )
        },
        {
          path: '/marketplaces',
          label: 'Marketplaces',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          )
        },
        {
          path: '/erps',
          label: 'ERPs',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )
        },
        {
          path: '/redes-sociais',
          label: 'APIs Oficiais',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          )
        }
      ]
    },
    {
      path: '/chat-crm',
      label: 'Chat CRM',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/chat-crm',
          label: 'Inbox',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          )
        },
        {
          path: '/respostas-rapidas',
          label: 'Respostas Rápidas',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          )
        }
      ]
    },
    {
      path: '/contatos',
      label: 'Contatos',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/contatos',
          label: 'Lista',
          icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
        },
        {
          path: '/contatos/kanban',
          label: 'Kanban',
          icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h4v14H4V5zm6 0h4v9h-4V5zm6 0h4v11h-4V5z" /></svg>
        },
        {
          path: '/contatos/etiquetas',
          label: 'Etiquetas',
          icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M3 3h8l10 10-8 8L3 11V3z" /></svg>
        }
      ]
    },
    {
      path: '/funis',
      label: 'Funis',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/funis',
          label: 'Construir / Editar',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          )
        },
        {
          path: '/test-chat',
          label: 'Teste do Funil',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          )
        }
      ]
    },
    {
      path: '/formularios',
      label: 'Formularios',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
        </svg>
      )
    },
    {
      path: '/follow-up',
      label: 'Follow-up',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      path: '/campanhas',
      label: 'Campanhas',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/campanhas-contatos',
          label: 'Campanhas para Contatos',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          )
        },
        {
          path: '/campanhas-grupos',
          label: 'Campanhas para Grupos',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )
        },
        {
          path: '/whatsapp-templates',
          label: 'Templates WhatsApp',
          icon: (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.885-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          )
        }
      ]
    },
    {
      path: '/prospeccao',
      label: 'Leads Prospecção',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/google-maps',
          label: 'Buscar no Google Maps',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        },
        {
          path: '/cnpj',
          label: 'Busca CNPJ',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          )
        },
        {
          path: '/erp-contatos',
          label: 'Buscar contato no ERP',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )
        },
        {
          path: '/extrator-grupos',
          label: 'Extrator de contatos de grupos',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )
        }
      ]
    },
    {
      path: '/automacoes',
      label: 'Automações',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/automacao-comentarios',
          label: 'Automação Comentários',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          )
        },
        {
          path: '/tiny-erp/automacao',
          label: 'Automação com ERP',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )
        },
        {
          path: '/automacoes-webhook',
          label: 'Automações com webhook',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-1.414 1.414a4 4 0 01-5.656-5.656l1.414-1.414m7.656 0l1.414-1.414a4 4 0 00-5.656-5.656l-1.414 1.414m5.656 5.656L8.172 17.828" />
            </svg>
          )
        }
      ]
    },
    {
      path: '/configuracoes',
      label: 'Configurações',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          path: '/configuracoes-ia',
          label: 'Configurações IA',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          )
        },
        {
          path: '/configuracoes-chat',
          label: 'Configurações CHAT',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )
        },
        {
          path: '/configuracoes-logs',
          label: 'Logs',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6h6v6m-8 4h10a2 2 0 002-2V7.414a2 2 0 00-.586-1.414l-2.414-2.414A2 2 0 0014.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          )
        }
      ]
    },
    ...(user?.role === 'SUPERADMIN' ? [{
      path: '/super-admin',
      label: 'Super Admin',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }] : []),
  ];

  const visibleMenuItems = menuItems.filter(item => {
    if (item.path === '/conexoes') return false;
    const permKey = CRM_MENU_KEYS[item.path];
    return !permKey || canSee(permKey);
  });

  return (
    <nav
      className={`sidebar-navigation w-64 max-w-[85vw] lg:w-52 shadow-lg flex flex-col transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      style={{ background: 'var(--sidebar-bg)' }}
    >
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-5">
        {/* Botão fechar (apenas mobile) */}
        <div className="mb-2 flex justify-end lg:hidden">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Fechar menu"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Menu Items */}
        <ul className="space-y-1">
          {visibleMenuItems.map((item) => {
            const getRef = () => {
              switch (item.path) {
                case '/funis': return funisRef;
                case '/conexoes': return connectionsRef;
                case '/prospeccao': return prospeccaoRef;
                case '/automacoes': return automacoesRef;
                case '/campanhas': return campanhasRef;
                case '/configuracoes': return configuracoesRef;
                case '/chat-crm': return chatCrmRef;
                case '/contatos': return contactsRef;
                default: return connectionsRef;
              }
            };

            const getShow = () => {
              switch (item.path) {
                case '/funis': return showFunis;
                case '/conexoes': return showConnections;
                case '/prospeccao': return showProspeccao;
                case '/automacoes': return showAutomacoes;
                case '/campanhas': return showCampanhas;
                case '/configuracoes': return showConfiguracoes;
                case '/chat-crm': return showChatCrm;
                case '/contatos': return showContacts;
                default: return false;
              }
            };

            const handleToggle = () => {
              setShowFunis(item.path === '/funis' ? !showFunis : false);
              setShowConnections(item.path === '/conexoes' ? !showConnections : false);
              setShowProspeccao(item.path === '/prospeccao' ? !showProspeccao : false);
              setShowAutomacoes(item.path === '/automacoes' ? !showAutomacoes : false);
              setShowCampanhas(item.path === '/campanhas' ? !showCampanhas : false);
              setShowConfiguracoes(item.path === '/configuracoes' ? !showConfiguracoes : false);
              setShowChatCrm(item.path === '/chat-crm' ? !showChatCrm : false);
              setShowContacts(item.path === '/contatos' ? !showContacts : false);
            };

            const handleClose = () => {
              setShowFunis(false);
              setShowConnections(false);
              setShowProspeccao(false);
              setShowAutomacoes(false);
              setShowCampanhas(false);
              setShowConfiguracoes(false);
              setShowChatCrm(false);
              setShowContacts(false);
            };

            const isSubmenuActive = item.hasSubmenu && item.submenu?.some(
              (sub: any) => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
            );

            return (
              <li key={item.path}>
                {item.hasSubmenu ? (
                  <div className="relative" ref={getRef()}>
                    <button
                      onClick={handleToggle}
                      className={`group relative flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200 ${
                        isSubmenuActive
                          ? 'bg-white shadow-lg'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                      style={isSubmenuActive ? { color: 'var(--astra-dark-blue)' } : undefined}
                      title={item.label}
                    >
                      <span className="flex-shrink-0">{item.icon}</span>
                      <span className="text-sm font-medium truncate">{item.label}</span>
                      <svg className="w-3 h-3 ml-auto flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Submenu (acordeão inline abaixo do item, em desktop e mobile) */}
                    {getShow() && (
                      <div className="relative mt-1 w-full rounded-lg bg-white py-2 shadow-xl z-[60]">
                        {item.submenu?.map((subItem: any) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                              location.pathname === subItem.path
                                ? 'text-blue-600 bg-blue-50'
                                : 'text-gray-700'
                            }`}
                            onClick={handleClose}
                          >
                            {subItem.icon}
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200 ${
                      (item as { isSpecial?: boolean }).isSpecial
                        ? 'hover:bg-white/10'
                        : location.pathname.startsWith(item.path)
                          ? 'bg-white shadow-lg'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    style={
                      (item as { isSpecial?: boolean }).isSpecial
                        ? { color: '#fbbf24' }
                        : location.pathname.startsWith(item.path)
                          ? { color: 'var(--astra-dark-blue)' }
                          : undefined
                    }
                    title={item.label}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>

    </nav>
  );
}
